import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import StorageIcon from "@mui/icons-material/Storage";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import LanguageIcon from "@mui/icons-material/Language";

function Home() {
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography variant="h2" component="h1" gutterBottom>
          John Cressey
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          AI Product Leader • LLM Systems Architect • Concept to Deployment
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Shanghai, China
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Professional Summary
        </Typography>
        <Typography paragraph>
          AI Product Leader with a proven track record of building and launching
          AI-powered products that drive millions of yuan in revenue. Expert in
          LLM prototyping, full-stack development, and strategic product vision,
          with deep experience leading cross-functional teams across
          engineering, content, and design. Fluent in Mandarin and skilled at
          navigating cross-cultural environments. Specializes in education
          technology and global AI deployment.
        </Typography>
      </Paper>

      <Typography variant="h4" gutterBottom sx={{ mt: 6, mb: 3 }}>
        Core Expertise
      </Typography>
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PsychologyIcon sx={{ mr: 1 }} />
              <Typography variant="h6">AI & Machine Learning</Typography>
            </Box>
            <Typography>
              Specialized in LLM Pipelines, Prompt Engineering, Multi-agent
              Systems, AI Content Generation, Language Evaluation, and
              Speech/Image Generation technologies.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <CodeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Development</Typography>
            </Box>
            <Typography>
              Proficient in Python, JavaScript, SQL, and frameworks including
              FastAPI, Flask, and React. Experienced with Docker, CI/CD, AWS,
              Azure, and API Integration.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <StorageIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Product Management</Typography>
            </Box>
            <Typography>
              Expert in AI Product Roadmapping, Market Fit, Technical Vision,
              Agile/Scrum methodologies, Metrics-Driven Development, and
              Cross-functional Team Leadership.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <WorkIcon sx={{ mr: 2, fontSize: 28 }} />
          <Typography variant="h4">Professional Experience</Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            LAIX - Liulishuo Education Technology
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Senior AI Product Manager and AI Solutions Lead | June 2023 –
            Present
          </Typography>
          <Typography paragraph>
            • Lead AI product strategy and development company-wide,
            architecting full-stack AI solutions (Python/Flask, React, LLM/ML
            APIs) to drive revenue growth, market positioning, and operational
            scale.
          </Typography>
          <Typography paragraph>
            • Oversee architecture and deployment of scalable microservices for
            content generation, analysis, and adaptive learning, directly
            impacting business value.
          </Typography>
          <Typography paragraph>
            • Direct cross-functional teams across engineering, design,
            curriculum, and QA; manage sprint cycles, align development to
            strategic objectives, and scale technical capabilities.
          </Typography>
          <Typography paragraph>
            • Two-time recipient of the "Curiosity" Award for consistent
            innovation, learning agility, and impactful contributions to company
            growth.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Selected Projects
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            IELTS Application – Project Director
          </Typography>
          <Typography paragraph>
            • Took ownership of a strategically important IELTS preparation app
            facing significant delays, successfully revitalizing development and
            launching within 3 months.
          </Typography>
          <Typography paragraph>
            • Defined product vision and oversaw full-stack logic, LLM prompt
            tuning, and front-end integration, introducing personalized
            dashboards and adaptive lesson-skip logic.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            2G School Initiative – AI Solutions Lead
          </Typography>
          <Typography paragraph>
            • Transformed a slow-growth B2G educational initiative by
            spearheading the integration of AI- powered tools, driving adoption
            from fewer than 50 schools to over 500 across Shanghai.
          </Typography>
          <Typography paragraph>
            • Architected and launched AI chatbot, content creation, and
            automated writing assessment tools.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Podcast Creation System – AI Solutions Lead
          </Typography>
          <Typography paragraph>
            • Designed and implemented an agent-based AI content pipeline,
            transforming written articles into fully produced, dynamic podcast
            episodes in minutes—reducing production time from 3 days to 3
            minutes and cutting costs by 85%.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Senior Curriculum and Product Designer | August 2018 – May 2023
          </Typography>
          <Typography paragraph>
            • Led curriculum team for flagship Kids AI product reaching 2M+
            users and ¥20M monthly sales.
          </Typography>
          <Typography paragraph>
            • Built machine-learning pronunciation scoring system with 95%
            accuracy.
          </Typography>
          <Typography paragraph>
            • Designed personalized content flows that increased retention by
            35% and LTV by 25%.
          </Typography>
        </Box>
      </Paper>

      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <SchoolIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Education</Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold">
              University of California, San Diego
            </Typography>
            <Typography>B.S. Cognitive Science (Neuroscience)</Typography>
            <Typography>B.A. Linguistics</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Honors: Outstanding Senior, UCSD Commencement Speaker
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <LanguageIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Languages</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip label="English (Native)" color="primary" />
              <Chip label="Mandarin Chinese (HSK6 – Fluent)" color="primary" />
            </Stack>
            <Typography variant="body2">
              Experienced in navigating cross-cultural environments and leading
              global teams.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Home;
